// GPU tilemap layer (replaces DrawLevel blits).

import { Container, Sprite, Texture, Rectangle } from 'pixi.js';
import type { Camera } from '../camera';
import { TILE_DRAW_MARGIN } from '../camera';
import { G, rooms } from '../../core/state';
import { BASE_TILE, TILE } from '../../core/constants';
import { Get, GetRoom } from '../../core/mapgen';
import { getImg, getAssetScale, makeFog } from '../assets';
import type { Surface } from '../surface';
import { surfaceToIndexTexture } from './index-texture';
import { profilerMeasure } from '../../dev/profiler';

const tr = Math.trunc;

let tilesSurf: Surface | null = null;
let fogSurf: Surface | null = null;
let tilesTex: Texture | null = null;
let fogTex: Texture | null = null;
const frameCache = new Map<string, Texture>();

const pool: Sprite[] = [];
const layer = new Container();

export function getTilemapLayer(): Container {
  return layer;
}

function ensureTileTextures(): void {
  if (tilesSurf === null) {
    tilesSurf = getImg('dat/i/tileset.png');
    fogSurf = makeFog(tilesSurf);
    tilesTex = surfaceToIndexTexture(tilesSurf);
    fogTex = surfaceToIndexTexture(fogSurf);
  }
}

function tileFrameTexture(tx: number, fog: boolean, srcTile: number): Texture {
  const key = `${fog ? 'f' : 't'}:${tx}:${srcTile}`;
  let t = frameCache.get(key);
  if (!t) {
    const base = fog ? fogTex! : tilesTex!;
    t = new Texture({
      source: base.source,
      frame: new Rectangle(tx, 0, srcTile, srcTile),
    });
    frameCache.set(key, t);
  }
  return t;
}

function acquireSprite(): Sprite {
  const s = pool.pop();
  if (s) {
    s.visible = true;
    return s;
  }
  return new Sprite();
}

function releaseSprites(): void {
  for (const c of [...layer.children]) {
    const s = c as Sprite;
    s.visible = false;
    layer.removeChild(s);
    pool.push(s);
  }
}

export function drawTilemapGpu(cam: Camera, hideNotVisited: number, fogOfWar: number): void {
  profilerMeasure('gpu.tilemap', () => {
    ensureTileTextures();
    releaseSprites();

    const tileScale = getAssetScale('dat/i/tileset.png');
    const srcTile = BASE_TILE * tileScale;
    const playerRoom = G.player_room;
    const m = TILE_DRAW_MARGIN;

    for (let y = -m; y < cam.visibleTilesY + m; y++) {
      for (let x = -m; x < cam.visibleTilesX + m; x++) {
        const resolve_x = x + cam.baseGX;
        const resolve_y = y + cam.baseGY;
        const groom = GetRoom(resolve_x, resolve_y);
        let tx: number;
        const visited = groom >= 0 ? rooms[groom].visited : 0;
        if (visited === 0 && playerRoom !== groom && hideNotVisited) {
          tx = 17 * srcTile;
        } else {
          tx = Get(resolve_x, resolve_y) * srcTile;
        }

        const sx = x * TILE - cam.tileOffX;
        const sy = y * TILE - cam.tileOffY;
        const useFog = !!(playerRoom !== groom && fogOfWar);

        const spr = acquireSprite();
        spr.texture = tileFrameTexture(tx, useFog, srcTile);
        spr.width = TILE;
        spr.height = TILE;
        spr.roundPixels = true;
        spr.position.set(Math.round(sx), Math.round(sy));
        layer.addChild(spr);
      }
    }
  });
}

export function clearTilemapGpu(): void {
  releaseSprites();
}
